// Edit-Operationen wie in Pro Tools (Reference Guide Kap. 26, 27, 29, 30, 31, 37, 39, 40, 42).
// Reine Funktionen auf dem Projekt: Sie werden innerhalb von commit() aufgerufen und sind damit ein Rückgängig-Schritt
// und gehen live an alle im Raum. Zeiten in Sekunden, MIDI-Noten in Beats relativ zum Clip.
import type { Clip, FadeShape, Project, Take, Track } from '@srs/shared';
import { uid } from '../state/demo.ts';
import { copyAuto, pasteAuto, shiftAfter, cutTimeAuto, removeRange as removeAutoRange, masterTrack } from './automation.ts';
import type { AutoPoint } from '@srs/shared';
import { clipMap, rebaseStart, warpLimits } from './warp.ts';
import { beatAt, gridSecAt, snapSec, spbAt, barAtSec, meterAtBar } from '@srs/shared';

export const EPS = 1e-6;
export type EditMode = 'shuffle' | 'slip' | 'spot' | 'grid';
export type Tool = 'smart' | 'trim' | 'select' | 'grab' | 'scrub' | 'pencil' | 'cut';
/** Edit-Auswahl: Zeitbereich auf bestimmten Spuren. start == end ist eine Einfügemarke. */
export interface Range { start: number; end: number; trackIds: string[] }
/** Zwischenablage: Clips je Spur, Zeiten relativ zum Anfang der Auswahl */
export interface Clipboard { length: number; tracks: Clip[][]; /** Automation je Spur (Automation folgt Edit) */ auto?: (Record<string, AutoPoint[]> | undefined)[] }
/** Länge der Audioquelle in Sekunden (aus dem geladenen Puffer) */
export type SrcLen = (sourceId: string) => number | undefined;

export const clipEnd = (c: Clip) => c.start + c.duration;
/** Viertel zwischen zwei Zeitpunkten (folgt der Tempo-Spur) */
const beatsIn = (p: Project, a: number, b: number) => beatAt(p, b) - beatAt(p, a);
export const clipsOn = (p: Project, trackId: string) => p.clips.filter((c) => c.trackId === trackId);
const overlaps = (c: Clip, s: number, e: number) => c.start < e - EPS && clipEnd(c) > s + EPS;
export const isEmptyRange = (r: Range) => r.end - r.start < EPS;

// ---------------------------------------------------------------------------
// Raster
export const GRIDS: { id: string; label: string; beats: number }[] = [
  { id: 'bar', label: '1 Takt', beats: -1 }, { id: '1/2', label: '1/2', beats: 2 }, { id: '1/4', label: '1/4', beats: 1 },
  { id: '1/8', label: '1/8', beats: 0.5 }, { id: '1/16', label: '1/16', beats: 0.25 }, { id: '1/32', label: '1/32', beats: 0.125 },
  { id: '1/8T', label: '1/8 Triole', beats: 1 / 3 }, { id: '1/16T', label: '1/16 Triole', beats: 1 / 6 },
];
export const NUDGES: { id: string; label: string; beats?: number; sec?: number }[] = [
  { id: '1/4', label: '1/4', beats: 1 }, { id: '1/8', label: '1/8', beats: 0.5 }, { id: '1/16', label: '1/16', beats: 0.25 },
  { id: '1/32', label: '1/32', beats: 0.125 }, { id: '100ms', label: '100 ms', sec: 0.1 }, { id: '10ms', label: '10 ms', sec: 0.01 }, { id: '1ms', label: '1 ms', sec: 0.001 },
];
/** Rasterweite in Sekunden an der Stelle at (folgt Tempo und Taktart) */
export function gridSec(p: Project, id: string, at = 0) {
  return gridSecAt(p, at, GRIDS.some((x) => x.id === id) ? id : '1/16');
}
export function nudgeSec(p: Project, id: string, at = 0) {
  const n = NUDGES.find((x) => x.id === id) ?? NUDGES[2];
  return n.sec ?? (n.beats ?? 0.25) * spbAt(p, at);
}
/** Auf das Raster ziehen (Takt und Unterteilungen nach der Tempo-Spur) */
export const snapGrid = (p: Project, t: number, id: string) => snapSec(p, t, GRIDS.some((x) => x.id === id) ? id : '1/16');

// ---------------------------------------------------------------------------
// Grundbausteine
const clampFades = (c: Clip) => {
  c.fadeIn = Math.max(0, Math.min(c.fadeIn, c.duration));
  c.fadeOut = Math.max(0, Math.min(c.fadeOut, c.duration - c.fadeIn));
};

/** Clip-Loop in einzelne Clips auflösen (Unloop). Gibt die Teile zurück. */
export function unloop(p: Project, c: Clip): Clip[] {
  if (!c.loop) return [c];
  const len = c.loop.length, idx = p.clips.indexOf(c), parts: Clip[] = [];
  const lenBeats = beatsIn(p, c.start, c.start + len);
  let t = 0, k = 0;
  while (t < c.duration - EPS) {
    const d = Math.min(len, c.duration - t), last = t + d >= c.duration - EPS;
    const part: Clip = { ...structuredClone(c), id: k === 0 ? c.id : uid('c'), start: c.start + t, duration: d, loop: undefined, fadeIn: k === 0 ? c.fadeIn : 0, fadeOut: last ? c.fadeOut : 0 };
    if (c.kind === 'midi') part.notes = (c.notes ?? []).filter((n) => n.t < lenBeats - 1e-9).map((n) => ({ ...n }));
    clampFades(part);
    parts.push(part); t += len; k++;
  }
  p.clips.splice(idx, 1, ...parts);
  return parts;
}

/** Clip an der Zeit t teilen (Separate). Gibt [links, rechts] zurück oder null, wenn t nicht im Clip liegt. */
export function splitAt(p: Project, clip: Clip, t: number): [Clip, Clip] | null {
  let c = clip;
  if (t <= c.start + EPS || t >= clipEnd(c) - EPS) return null;
  if (c.loop) {
    const parts = unloop(p, c);
    const hit = parts.find((x) => t > x.start + EPS && t < clipEnd(x) - EPS);
    if (!hit) { const i = parts.findIndex((x) => Math.abs(x.start - t) < EPS); return i > 0 ? [parts[i - 1], parts[i]] : null; }
    c = hit;
  }
  const cut = t - c.start;
  const right: Clip = { ...structuredClone(c), id: uid('c'), start: t, duration: c.duration - cut, fadeIn: 0, fadeOut: c.fadeOut };
  if (c.kind === 'audio') { if (c.warp) { right.start = c.start; rebaseStart(p, right, t); right.start = t; } else right.offset = c.offset + cut; }
  else {
    const cb = beatsIn(p, c.start, t), notes = c.notes ?? [];
    right.notes = notes.filter((n) => n.t >= cb - 1e-9).map((n) => ({ ...n, t: n.t - cb }));
    c.notes = notes.filter((n) => n.t < cb - 1e-9);
  }
  c.duration = cut; c.fadeOut = 0;
  // Sync Point bleibt in dem Teil, in dem er liegt
  if (c.syncPoint !== undefined) { if (c.syncPoint >= cut - EPS) { right.syncPoint = c.syncPoint - cut; delete c.syncPoint; } else delete right.syncPoint; }
  clampFades(c); clampFades(right);
  p.clips.splice(p.clips.indexOf(c) + 1, 0, right);
  return [c, right];
}

/** Alle Clips der Spuren an t teilen */
export function splitTracksAt(p: Project, trackIds: string[], t: number) {
  for (const c of p.clips.filter((x) => trackIds.includes(x.trackId) && t > x.start + EPS && t < clipEnd(x) - EPS)) splitAt(p, c, t);
}

/** Anfang und Ende eines Clips neu setzen, Inhalt bleibt an seiner Zeit (Trim) */
export function setBounds(p: Project, c: Clip, newStart: number, newEnd: number) {
  const d = newStart - c.start;
  if (Math.abs(d) > EPS) {
    if (c.loop) { c.loop = undefined; }
    if (c.kind === 'audio') { if (c.warp) rebaseStart(p, c, newStart); else c.offset += d; }
    else { const db = beatsIn(p, c.start, newStart); c.notes = (c.notes ?? []).map((n) => ({ ...n, t: n.t - db })); }
  }
  c.start = newStart; c.duration = Math.max(EPS * 10, newEnd - newStart);
  if (c.syncPoint !== undefined) { c.syncPoint -= d; if (c.syncPoint < -EPS || c.syncPoint > c.duration + EPS) delete c.syncPoint; }
  clampFades(c);
}

/** Grenzen, bis zu denen ein Audio-Clip ausgezogen werden kann (Quelle reicht so weit) */
export function trimLimits(c: Clip, srcLen: SrcLen, p?: Project): { minStart: number; maxEnd: number } {
  if (c.kind !== 'audio' || !c.sourceId) return { minStart: 0, maxEnd: Infinity };
  const len = srcLen(c.sourceId);
  if (c.warp && p) return warpLimits(p, c, len);
  return { minStart: Math.max(0, c.start - c.offset), maxEnd: c.loop ? Infinity : len === undefined ? clipEnd(c) : c.start - c.offset + len };
}

// ---------------------------------------------------------------------------
// Grundbefehle (Kap. 26)

/** Clear: Auswahl entfernen. In Shuffle rücken die folgenden Clips nach. */
export function clearRange(p: Project, r: Range, shuffle: boolean) {
  if (isEmptyRange(r)) return;
  splitTracksAt(p, r.trackIds, r.start);
  splitTracksAt(p, r.trackIds, r.end);
  p.clips = p.clips.filter((c) => !(r.trackIds.includes(c.trackId) && c.start >= r.start - EPS && clipEnd(c) <= r.end + EPS));
  if (shuffle) {
    const len = r.end - r.start;
    for (const c of p.clips) if (r.trackIds.includes(c.trackId) && c.start >= r.end - EPS) c.start -= len;
  }
  // Automation folgt Edit
  for (const t of [...p.tracks, ...(r.trackIds.includes('master') ? [masterTrack(p)] : [])]) if (r.trackIds.includes(t.id) && t.automation) {
    if (shuffle) cutTimeAuto(t, r.start, r.end);
    else for (const param of Object.keys(t.automation.lanes)) removeAutoRange(t, param, r.start, r.end, true);
  }
}

/** Teile eines Clips innerhalb [s, e] als Kopie (ohne das Projekt zu ändern) */
function sliceClip(p: Project, c: Clip, s: number, e: number): Clip[] {
  const tmp = { ...p, clips: [structuredClone(c)] } as Project;
  for (const x of [...tmp.clips]) unloop(tmp, x);
  splitTracksAt(tmp, [c.trackId], s);
  splitTracksAt(tmp, [c.trackId], e);
  return tmp.clips.filter((x) => x.start >= s - EPS && clipEnd(x) <= e + EPS);
}

export function copyRange(p: Project, r: Range): Clipboard {
  return {
    length: r.end - r.start,
    tracks: r.trackIds.map((id) => clipsOn(p, id).filter((c) => overlaps(c, r.start, r.end))
      .flatMap((c) => sliceClip(p, c, r.start, r.end)).map((c) => ({ ...c, start: c.start - r.start }))),
    auto: r.trackIds.map((id) => { const t = p.tracks.find((x) => x.id === id); return t ? copyAuto(t, r.start, r.end) : undefined; }),
  };
}

/** Zwischenablage an `at` einfügen. Shuffle schiebt Folgendes nach hinten, sonst wird überschrieben. */
export function pasteClipboard(p: Project, cb: Clipboard, at: number, trackIds: string[], shuffle: boolean): Range {
  const used: string[] = [];
  const groupMap = new Map<string, string>();
  cb.tracks.forEach((clips, i) => {
    const tid = trackIds[i];
    const t = tid ? p.tracks.find((x) => x.id === tid) : undefined;
    if (!t) return;
    const fit = clips.filter((c) => (c.kind === 'midi') === (t.kind === 'midi'));
    used.push(tid);
    if (shuffle) {
      splitTracksAt(p, [tid], at);
      for (const c of p.clips) if (c.trackId === tid && c.start >= at - EPS) c.start += cb.length;
    } else clearRange(p, { start: at, end: at + cb.length, trackIds: [tid] }, false);
    for (const c of fit) p.clips.push({ ...structuredClone(c), id: uid('c'), trackId: tid, start: at + c.start, groupId: c.groupId ? groupMap.get(c.groupId) ?? groupMap.set(c.groupId, uid('g')).get(c.groupId) : undefined });
    const auto = cb.auto?.[i];
    if (shuffle && t.automation) shiftAfter(t, at, cb.length);
    if (auto) pasteAuto(t, structuredClone(auto), at, cb.length);
  });
  return { start: at, end: at + cb.length, trackIds: used.length ? used : trackIds };
}

/** Paste: ersetzt eine Auswahl oder fügt an der Einfügemarke ein */
export function paste(p: Project, cb: Clipboard, r: Range, shuffle: boolean): Range {
  if (!isEmptyRange(r)) clearRange(p, r, shuffle);
  return pasteClipboard(p, cb, r.start, r.trackIds, shuffle);
}

/** Duplicate: Auswahl direkt dahinter noch einmal. Die neue Auswahl liegt auf der Kopie. */
export function duplicate(p: Project, r: Range, shuffle: boolean): Range {
  if (isEmptyRange(r)) return r;
  return pasteClipboard(p, copyRange(p, r), r.end, r.trackIds, shuffle);
}

/** Repeat: Auswahl n-mal hintereinander */
export function repeat(p: Project, r: Range, n: number, shuffle: boolean): Range {
  let cur = r;
  const cb = copyRange(p, r);
  for (let i = 0; i < n; i++) cur = pasteClipboard(p, cb, cur.end, r.trackIds, shuffle);
  return { start: r.start, end: cur.end, trackIds: r.trackIds };
}

/** Repeat to Fill Selection: Zwischenablage füllt die Auswahl, das letzte Stück wird gekürzt */
export function repeatToFill(p: Project, cb: Clipboard, r: Range) {
  if (isEmptyRange(r) || cb.length < EPS) return;
  clearRange(p, r, false);
  for (let at = r.start; at < r.end - EPS; at += cb.length) {
    const piece = Math.min(cb.length, r.end - at);
    const part: Clipboard = piece < cb.length - EPS
      ? { length: piece, tracks: cb.tracks.map((clips) => clips.flatMap((c) => (c.start < piece - EPS ? sliceClip(p, c, 0, piece) : []))) }
      : cb;
    pasteClipboard(p, part, at, r.trackIds, false);
  }
}

/** Heal Separation: aneinanderliegende Teile desselben Materials wieder zu einem Clip */
export function heal(p: Project, r: Range): number {
  let healed = 0;
  for (const tid of r.trackIds) {
    let again = true;
    while (again) {
      again = false;
      const list = clipsOn(p, tid).sort((a, b) => a.start - b.start);
      for (let i = 0; i < list.length - 1; i++) {
        const a = list[i], b = list[i + 1], seam = clipEnd(a);
        if (Math.abs(seam - b.start) > 1e-4 || seam < r.start - 1e-4 || seam > r.end + 1e-4 || a.kind !== b.kind || a.loop || b.loop) continue;
        if (a.kind === 'audio' && (a.sourceId !== b.sourceId || !!a.warp !== !!b.warp || Math.abs(b.offset - (a.warp ? clipMap(p, a).toSrc(seam) : a.offset + a.duration)) > 1e-4 || (a.warp?.pitch ?? 0) !== (b.warp?.pitch ?? 0) || (a.gainDb ?? 0) !== (b.gainDb ?? 0) || !!a.muted !== !!b.muted)) continue;
        if (a.warp && b.warp) { const sb = beatsIn(p, a.start, b.start); a.warp = { ...a.warp, points: [...a.warp.points.filter((w) => w.b < sb - 1e-9), ...b.warp.points.filter((w) => w.b >= -1e-9).map((w) => ({ s: w.s, b: w.b + sb }))] }; }
        if (a.kind === 'midi') { const ab = beatsIn(p, a.start, b.start); a.notes = [...(a.notes ?? []), ...(b.notes ?? []).map((n) => ({ ...n, t: n.t + ab }))]; }
        a.duration += b.duration; a.fadeOut = b.fadeOut;
        p.clips.splice(p.clips.indexOf(b), 1);
        healed++; again = true; break;
      }
    }
  }
  return healed;
}

/** Trim Clip to Selection */
export function trimToRange(p: Project, r: Range) {
  for (const c of p.clips.filter((x) => r.trackIds.includes(x.trackId) && overlaps(x, r.start, r.end))) {
    const s = Math.max(c.start, r.start), e = Math.min(clipEnd(c), r.end);
    if (c.loop && s > c.start + EPS) { for (const part of unloop(p, c)) if (overlaps(part, s, e)) setBounds(p, part, Math.max(part.start, s), Math.min(clipEnd(part), e)); else p.clips.splice(p.clips.indexOf(part), 1); }
    else setBounds(p, c, s, e);
  }
}
/** Trim Start / Trim End bis zur Einfügemarke (Tasten A und S) */
export function trimStartTo(p: Project, trackIds: string[], t: number) {
  for (const c of p.clips.filter((x) => trackIds.includes(x.trackId) && t > x.start + EPS && t < clipEnd(x) - EPS)) {
    if (c.loop) { const part = unloop(p, c).find((x) => t > x.start + EPS && t < clipEnd(x) - EPS); if (part) setBounds(p, part, t, clipEnd(part)); }
    else setBounds(p, c, t, clipEnd(c));
  }
}
export function trimEndTo(p: Project, trackIds: string[], t: number) {
  for (const c of p.clips.filter((x) => trackIds.includes(x.trackId) && t > x.start + EPS && t < clipEnd(x) - EPS)) { c.duration = t - c.start; clampFades(c); }
}

/** Insert Silence: Lücke in Länge der Auswahl einfügen, Folgendes rückt nach hinten */
export function insertSilence(p: Project, r: Range) {
  const len = r.end - r.start;
  if (len < EPS) return;
  splitTracksAt(p, r.trackIds, r.start);
  for (const c of p.clips) if (r.trackIds.includes(c.trackId) && c.start >= r.start - EPS) c.start += len;
  for (const t of p.tracks) if (r.trackIds.includes(t.id)) shiftAfter(t, r.start, len);
}

// ---------------------------------------------------------------------------
// Zeit-Operationen (Kap. 37): auf allen Spuren, mit Markern, Akkorden, Takes und Loop
function shiftTime(p: Project, at: number, len: number) {
  for (const m of p.markers) {
    if (m.time >= at - EPS) { m.time += len; if (m.end !== undefined) m.end += len; }
    else if (m.end !== undefined && m.end > at + EPS) m.end += len;
  }
  for (const ch of p.chords) if (ch.time >= at - EPS) ch.time += len;
  for (const t of p.takes) if (t.start >= at - EPS) t.start += len;
  if (p.loop.start >= at - EPS) p.loop.start += len;
  if (p.loop.end >= at - EPS) p.loop.end += len;
}
/** Tempo- und Taktwechsel hinter einer Stelle verschieben (Takte einfügen/entfernen) */
function shiftTempoMap(p: Project, at: number, len: number) {
  if (!p.tempos?.length && !p.meters?.length) return;
  const b0 = beatAt(p, at), db = len / spbAt(p, at), bar0 = barAtSec(p, at), dBar = db / meterAtBar(p, Math.floor(bar0)).barLen;
  if (len > 0) {
    for (const e of p.tempos ?? []) if (e.beat >= b0 - 1e-6) e.beat += db;
    for (const m of p.meters ?? []) if (m.bar >= bar0 - 1e-6) m.bar = Math.round(m.bar + dBar);
  } else {
    p.tempos = (p.tempos ?? []).filter((e) => !(e.beat >= b0 + db - 1e-6 && e.beat < b0 - 1e-6)).map((e) => (e.beat >= b0 - 1e-6 ? { ...e, beat: e.beat + db } : e)).filter((e) => e.beat > 1e-6);
    p.meters = (p.meters ?? []).filter((m) => !(m.bar >= bar0 + dBar - 1e-6 && m.bar < bar0 - 1e-6)).map((m) => (m.bar >= bar0 - 1e-6 ? { ...m, bar: Math.round(m.bar + dBar) } : m)).filter((m) => m.bar > 0);
  }
}
export function insertTime(p: Project, at: number, len: number) {
  if (len < EPS) return;
  shiftTempoMap(p, at, len);
  const ids = p.tracks.map((t) => t.id);
  splitTracksAt(p, ids, at);
  for (const c of p.clips) if (c.start >= at - EPS) c.start += len;
  for (const t of p.tracks) shiftAfter(t, at, len);
  if (p.masterAutomation) shiftAfter(masterTrack(p), at, len);
  shiftTime(p, at, len);
  forPlaylists(p, (q) => { splitTracksAt(q, ids, at); for (const c of q.clips) if (c.start >= at - EPS) c.start += len; });
}
export function cutTime(p: Project, s: number, e: number) {
  const len = e - s;
  if (len < EPS) return;
  shiftTempoMap(p, e, -len);
  const ids = p.tracks.map((t) => t.id);
  clearRange(p, { start: s, end: e, trackIds: ids }, true);
  if (p.masterAutomation) cutTimeAuto(masterTrack(p), s, e);
  p.markers = p.markers.filter((m) => !(m.time >= s - EPS && m.time < e - EPS));
  for (const m of p.markers) { if (m.time >= e - EPS) m.time -= len; if (m.end !== undefined) m.end = m.end >= e ? m.end - len : Math.min(m.end, s); }
  p.chords = p.chords.filter((c) => !(c.time >= s - EPS && c.time < e - EPS));
  for (const ch of p.chords) if (ch.time >= e - EPS) ch.time -= len;
  for (const t of p.takes) if (t.start >= e - EPS) t.start -= len;
  const lp = (x: number) => (x >= e ? x - len : x > s ? s : x);
  p.loop.start = lp(p.loop.start); p.loop.end = Math.max(p.loop.start + 0.1, lp(p.loop.end));
  forPlaylists(p, (q) => clearRange(q, { start: s, end: e, trackIds: ids }, true));
}
/** Hilfsfunktion: dieselbe Operation auf den nicht aktiven Playlists */
function forPlaylists(p: Project, fn: (q: Project) => void) {
  for (const [tid, st] of Object.entries(p.playlists ?? {})) for (const pl of st.others) {
    const q = { ...p, clips: pl.clips.map((c) => ({ ...c, trackId: tid })), playlists: undefined } as Project;
    fn(q); pl.clips = q.clips;
  }
}

// ---------------------------------------------------------------------------
// Clips bewegen (Kap. 29, 39)
export function nudge(p: Project, ids: string[], delta: number) {
  for (const c of p.clips) if (ids.includes(c.id) && !c.locked) c.start = Math.max(0, c.start + delta);
}
/** Clip-Anfänge aufs Raster (Quantize Clips to Grid) */
/**
 * Clips aufs Raster: der Ankerpunkt (Sync Point bzw. erkannter Anschlag, sonst der Clip-Anfang) landet auf dem Raster.
 * strength 0–1 zieht nur anteilig hin (natürlicher), Clips derselben Spur behalten ihre Reihenfolge.
 */
export function quantizeClips(p: Project, ids: string[], grid: string, o: { anchor?: (c: Clip) => number; strength?: number } = {}) {
  const k = Math.max(0, Math.min(1, o.strength ?? 1));
  let n = 0;
  for (const c of p.clips) {
    if (!ids.includes(c.id) || c.locked) continue;
    const a = o.anchor ? o.anchor(c) : 0, at = c.start + a;
    const target = snapGrid(p, at, grid), ns = Math.max(0, c.start + (target - at) * k);
    if (Math.abs(ns - c.start) > EPS) n++;
    c.start = ns;
  }
  return n;
}
/** Ankerpunkt eines Clips fürs Einrasten: Sync Point, sonst der erste Anschlag kurz nach dem Anfang (Vorlauf vor der Attacke), sonst 0 */
export function syncOf(c: Clip, transients?: number[] | null): number {
  if (c.syncPoint !== undefined) return c.syncPoint;
  if (c.kind !== 'audio' || !transients) return 0;
  const lim = Math.min(0.15, c.duration / 2);
  const t = transients.find((x) => x >= -EPS && x <= lim);
  return t !== undefined ? Math.max(0, t) : 0;
}
/** Verschieben im Shuffle-Modus: Lücke schließen, an der nächsten Clip-Grenze einreihen */
export function shuffleMove(p: Project, id: string, trackId: string, dropTime: number) {
  const c = p.clips.find((x) => x.id === id);
  if (!c || c.locked) return;
  const len = c.duration;
  for (const x of clipsOn(p, c.trackId)) if (x !== c && x.start >= clipEnd(c) - EPS) x.start -= len;
  c.trackId = trackId;
  const others = clipsOn(p, trackId).filter((x) => x !== c);
  const bounds = [0, ...others.flatMap((x) => [x.start, clipEnd(x)])];
  const b = bounds.reduce((best, x) => (Math.abs(x - dropTime) < Math.abs(best - dropTime) ? x : best), 0);
  for (const x of others) if (x.start >= b - EPS) x.start += len;
  c.start = b;
}
/** Trimmen im Shuffle-Modus: folgende Clips rücken um die Änderung mit */
export function shuffleTrim(p: Project, id: string, edge: 'start' | 'end', delta: number) {
  const c = p.clips.find((x) => x.id === id);
  if (!c) return;
  const oldEnd = clipEnd(c);
  if (edge === 'end') c.duration = Math.max(0.001, c.duration + delta);
  else {
    // Anfang kürzen: Der Clip bleibt stehen, sein Inhalt beginnt später, alles Folgende rückt nach
    const keep = c.start;
    setBounds(p, c, c.start + delta, oldEnd);
    c.start = keep;
  }
  clampFades(c);
  const change = clipEnd(c) - oldEnd;
  for (const x of clipsOn(p, c.trackId)) if (x !== c && x.start >= oldEnd - EPS) x.start += change;
}

// ---------------------------------------------------------------------------
// Clip-Eigenschaften
export function toggleMute(p: Project, ids: string[]) {
  const sel = p.clips.filter((c) => ids.includes(c.id));
  const to = sel.some((c) => !c.muted);
  for (const c of sel) c.muted = to;
  return to;
}
export function toggleLock(p: Project, ids: string[]) {
  const sel = p.clips.filter((c) => ids.includes(c.id));
  const to = sel.some((c) => !c.locked);
  for (const c of sel) c.locked = to;
  return to;
}
export function setClipGain(p: Project, ids: string[], db: number, relative = false) {
  for (const c of p.clips) if (ids.includes(c.id)) c.gainDb = Math.max(-36, Math.min(36, Math.round(((relative ? c.gainDb ?? 0 : 0) + db) * 10) / 10));
}
/** Normalisieren über Clip Gain (zerstörungsfrei): Spitze auf target dBFS */
export function normalize(p: Project, ids: string[], peakOf: (c: Clip) => number, target = -0.1) {
  for (const c of p.clips) if (ids.includes(c.id) && c.kind === 'audio') { const pk = peakOf(c); if (pk > 1e-6) c.gainDb = Math.round((target - 20 * Math.log10(pk)) * 10) / 10; }
}
export function renameClips(p: Project, ids: string[], name: string, numbered: boolean) {
  let n = 1;
  for (const c of p.clips) if (ids.includes(c.id)) c.name = numbered && ids.length > 1 ? `${name} ${String(n++).padStart(2, '0')}` : name;
}

// ---------------------------------------------------------------------------
// Clip Groups (Kap. 40): zusammen auswählen und bewegen
export function groupClips(p: Project, ids: string[]): string | null {
  if (ids.length < 2) return null;
  const g = uid('g');
  for (const c of p.clips) if (ids.includes(c.id)) c.groupId = g;
  return g;
}
export function ungroupClips(p: Project, ids: string[]) {
  const groups = new Set(p.clips.filter((c) => ids.includes(c.id) && c.groupId).map((c) => c.groupId));
  for (const c of p.clips) if (c.groupId && groups.has(c.groupId)) c.groupId = undefined;
  return groups.size;
}
/** Auswahl um alle Clips derselben Gruppen erweitern */
export const withGroups = (p: Project, ids: string[]) => {
  const groups = new Set(p.clips.filter((c) => ids.includes(c.id) && c.groupId).map((c) => c.groupId));
  return groups.size ? [...new Set([...ids, ...p.clips.filter((c) => c.groupId && groups.has(c.groupId)).map((c) => c.id)])] : ids;
};

// ---------------------------------------------------------------------------
// Clip-Loops (Kap. 40)
export function loopClip(p: Project, id: string, opts: { count?: number; length?: number }) {
  const c = p.clips.find((x) => x.id === id);
  if (!c || c.warp) return;   // Elastic-Clips lassen sich nicht loopen (wie in Pro Tools: erst Elastic aus oder Repeat)
  const len = c.loop?.length ?? c.duration;
  c.loop = { length: len };
  c.duration = opts.length ?? Math.max(1, Math.round(opts.count ?? 2)) * len;
  c.fadeOut = Math.min(c.fadeOut, len);
}
export function unloopClip(p: Project, id: string) {
  const c = p.clips.find((x) => x.id === id);
  if (c?.loop) unloop(p, c);
}

// ---------------------------------------------------------------------------
// Fades und Crossfades (Kap. 30)
export interface FadeOpts { inShape: FadeShape; outShape: FadeShape; xShape: FadeShape }
export const DEFAULT_FADES: FadeOpts = { inShape: 'sCurve', outShape: 'sCurve', xShape: 'equalPower' };

/** Fades aus einer Auswahl: über einem Clip-Anfang ein Fade-In, über einem Ende ein Fade-Out, über einer Schnittstelle ein Crossfade */
export function applyFades(p: Project, r: Range, o: FadeOpts, srcLen: SrcLen): { fadeIns: number; fadeOuts: number; crossfades: number } {
  const out = { fadeIns: 0, fadeOuts: 0, crossfades: 0 };
  if (isEmptyRange(r)) return out;
  for (const tid of r.trackIds) {
    const list = clipsOn(p, tid).filter((c) => c.kind === 'audio').sort((a, b) => a.start - b.start);
    const done = new Set<string>();
    for (let i = 0; i < list.length - 1; i++) {
      const a = list[i], b = list[i + 1], seam = clipEnd(a);
      if (Math.abs(seam - b.start) > 0.0005 || seam <= r.start + EPS || seam >= r.end - EPS) continue;
      // Handles: wie weit reicht das Material über die Schnittstelle hinaus?
      const la = trimLimits(a, srcLen, p), lb = trimLimits(b, srcLen, p);
      const right = Math.min(r.end - seam, la.maxEnd - seam), left = Math.min(seam - r.start, b.start - lb.minStart);
      if (right > 0.001 && left > 0.001) {
        const xs = seam - left, xe = seam + right;
        a.duration = xe - a.start;
        setBounds(p, b, xs, clipEnd(b));
        a.fadeOut = xe - xs; b.fadeIn = xe - xs;
        a.fadeOutShape = o.xShape; b.fadeInShape = o.xShape;
      } else {
        // kein Material über die Schnittstelle: Fade-Out und Fade-In treffen sich an der Schnittstelle
        a.fadeOut = Math.min(a.duration, seam - r.start); b.fadeIn = Math.min(b.duration, r.end - seam);
        a.fadeOutShape = o.xShape; b.fadeInShape = o.xShape;
      }
      clampFades(a); clampFades(b);
      done.add(`${a.id}:out`); done.add(`${b.id}:in`);
      out.crossfades++;
    }
    for (const c of list) {
      const e = clipEnd(c);
      if (!done.has(`${c.id}:in`) && r.start <= c.start + 0.0005 && r.end > c.start + EPS && r.end < e - EPS) { c.fadeIn = r.end - c.start; c.fadeInShape = o.inShape; clampFades(c); out.fadeIns++; }
      else if (!done.has(`${c.id}:out`) && r.end >= e - 0.0005 && r.start > c.start + EPS && r.start < e - EPS) { c.fadeOut = e - r.start; c.fadeOutShape = o.outShape; clampFades(c); out.fadeOuts++; }
    }
  }
  return out;
}
/** Fade-In vom Clip-Anfang bis t (Taste D), Fade-Out von t bis Clip-Ende (Taste G) */
export function fadeTo(p: Project, trackIds: string[], t: number, which: 'in' | 'out', shape: FadeShape) {
  let n = 0;
  for (const c of p.clips) if (trackIds.includes(c.trackId) && c.kind === 'audio' && t > c.start + EPS && t < clipEnd(c) - EPS) {
    if (which === 'in') { c.fadeIn = t - c.start; c.fadeInShape = shape; } else { c.fadeOut = clipEnd(c) - t; c.fadeOutShape = shape; }
    clampFades(c); n++;
  }
  return n;
}
/** Fades in der Auswahl entfernen */
export function removeFades(p: Project, r: Range) {
  for (const c of p.clips) if (r.trackIds.includes(c.trackId) && overlaps(c, r.start - 0.001, r.end + 0.001)) {
    if (c.start >= r.start - 0.001 && c.start <= r.end) c.fadeIn = 0;
    if (clipEnd(c) >= r.start && clipEnd(c) <= r.end + 0.001) c.fadeOut = 0;
  }
}

// ---------------------------------------------------------------------------
// Strip Silence (Kap. 39). Die Erkennung liefert je Clip die Bereiche mit Signal (Timeline-Zeiten).
export type StripMode = 'strip' | 'extract' | 'separate';
export function stripSilence(p: Project, r: Range, sound: Map<string, [number, number][]>, mode: StripMode, naming?: { name: string; start: number; zeros: number; suffix: string }): number {
  let created = 0, num = naming?.start ?? 1;
  for (const [clipId, regions] of sound) {
    const c = p.clips.find((x) => x.id === clipId);
    if (!c || !r.trackIds.includes(c.trackId)) continue;
    const ws = Math.max(c.start, r.start), we = Math.min(clipEnd(c), r.end);
    if (we - ws < EPS) continue;
    const inside = regions.map(([a, b]) => [Math.max(a, ws), Math.min(b, we)] as [number, number]).filter(([a, b]) => b - a > EPS);
    const cuts = [...new Set([ws, we, ...inside.flat()].map((x) => Math.round(x * 1e6) / 1e6))].sort((a, b) => a - b);
    const tid = c.trackId;
    for (const t of cuts) splitTracksAt(p, [tid], t);
    const pieces = clipsOn(p, tid).filter((x) => x.start >= ws - EPS && clipEnd(x) <= we + EPS).sort((a, b) => a.start - b.start);
    for (const piece of pieces) {
      const mid = piece.start + piece.duration / 2;
      const loud = inside.some(([a, b]) => mid >= a && mid <= b);
      const remove = mode === 'strip' ? !loud : mode === 'extract' ? loud : false;
      if (remove) { p.clips.splice(p.clips.indexOf(piece), 1); continue; }
      if (naming && (mode !== 'extract' ? loud : !loud)) piece.name = `${naming.name}${String(num++).padStart(naming.zeros + 1, '0')}${naming.suffix}`;
      created++;
    }
  }
  return created;
}

// ---------------------------------------------------------------------------
// Comping (Kap. 31): Ausschnitt eines Takes oder einer anderen Playlist in die aktive Playlist kopieren
export function compTake(p: Project, track: Track, take: Take, r: { start: number; end: number }) {
  const s = Math.max(r.start, take.start), e = Math.min(r.end, take.start + take.duration);
  if (e - s < 0.001 || !take.sourceId) return null;
  clearRange(p, { start: s, end: e, trackIds: [track.id] }, false);
  const c: Clip = { id: uid('c'), trackId: track.id, kind: 'audio', name: `${track.name} Take ${take.n}`, start: s, offset: (take.offset ?? 0) + s - take.start, duration: e - s, sourceId: take.sourceId, takeId: take.id, fadeIn: 0.004, fadeOut: 0.004, by: { userId: take.by, at: take.createdAt } };
  p.clips.push(c);
  return c;
}

/** Takes löschen, samt aller Clips, die aus ihnen geschnitten wurden (auch in anderen Playlists). Gibt die Zahl der entfernten Clips zurück. */
export function deleteTakes(p: Project, takeIds: string[]): number {
  const ids = new Set(takeIds);
  const before = p.clips.length;
  p.takes = p.takes.filter((t) => !ids.has(t.id));
  p.clips = p.clips.filter((c) => !(c.takeId && ids.has(c.takeId)));
  let n = before - p.clips.length;
  for (const st of Object.values(p.playlists ?? {})) for (const pl of st.others) { const b = pl.clips.length; pl.clips = pl.clips.filter((c) => !(c.takeId && ids.has(c.takeId))); n += b - pl.clips.length; }
  for (const k of Object.keys(p.comps ?? {})) p.comps[k] = p.comps[k].filter((x) => !ids.has(x.takeId));
  return n;
}
/** Takes einer Spur, die nirgends verwendet werden (weder in der Haupt-Playlist noch in anderen Playlists) */
export function unusedTakes(p: Project, trackId: string): Take[] {
  const used = new Set<string>();
  for (const c of p.clips) if (c.takeId) used.add(c.takeId);
  for (const st of Object.values(p.playlists ?? {})) for (const pl of st.others) for (const c of pl.clips) if (c.takeId) used.add(c.takeId);
  return p.takes.filter((t) => t.trackId === trackId && !used.has(t.id));
}
/** Nächste Take-Nummer einer Spur (nach dem Löschen nicht doppelt vergeben) */
export const nextTakeNo = (p: Project, trackId: string) => p.takes.filter((t) => t.trackId === trackId).reduce((m, t) => Math.max(m, t.n), 0) + 1;

// ---------------------------------------------------------------------------
// Playlists (Kap. 31)
export function playlistState(p: Project, t: Track) { return p.playlists?.[t.id] ?? { active: t.name, others: [] }; }
function nextPlaylistName(base: string, used: string[]) {
  for (let i = 1; i < 1000; i++) { const n = `${base}.${String(i).padStart(2, '0')}`; if (!used.includes(n)) return n; }
  return `${base}.${Date.now()}`;
}
/** Neue Playlist (leer) oder Duplikat der aktiven */
export function newPlaylist(p: Project, trackId: string, dup: boolean) {
  const t = p.tracks.find((x) => x.id === trackId);
  if (!t) return;
  p.playlists ??= {};
  const st = (p.playlists[trackId] ??= { active: t.name, others: [] });
  const cur = clipsOn(p, trackId);
  st.others.push({ id: uid('pl'), name: st.active, clips: structuredClone(cur) });
  if (!dup) p.clips = p.clips.filter((c) => c.trackId !== trackId);
  else for (const c of cur) c.id = uid('c');
  st.active = nextPlaylistName(t.name, [st.active, ...st.others.map((o) => o.name)]);
}
export function switchPlaylist(p: Project, trackId: string, playlistId: string) {
  const st = p.playlists?.[trackId];
  const i = st?.others.findIndex((o) => o.id === playlistId) ?? -1;
  if (!st || i < 0) return;
  const target = st.others[i];
  const cur = clipsOn(p, trackId);
  st.others[i] = { id: uid('pl'), name: st.active, clips: structuredClone(cur) };
  p.clips = [...p.clips.filter((c) => c.trackId !== trackId), ...target.clips.map((c) => ({ ...c, trackId }))];
  st.active = target.name;
}
export function deletePlaylist(p: Project, trackId: string, playlistId: string) {
  const st = p.playlists?.[trackId];
  if (st) st.others = st.others.filter((o) => o.id !== playlistId);
}
/** Ausschnitt einer anderen Playlist in die aktive kopieren (Copy Selection to Main Playlist) */
export function compPlaylist(p: Project, trackId: string, playlistId: string, r: { start: number; end: number }) {
  const pl = p.playlists?.[trackId]?.others.find((o) => o.id === playlistId);
  if (!pl) return;
  const tmp = { ...p, clips: pl.clips.map((c) => ({ ...c, trackId })) } as Project;
  const cb = copyRange(tmp, { start: r.start, end: r.end, trackIds: [trackId] });
  clearRange(p, { start: r.start, end: r.end, trackIds: [trackId] }, false);
  pasteClipboard(p, cb, r.start, [trackId], false);
}

// ---- Spuren löschen und duplizieren ----
/** Spuren löschen mit allem, was zu ihnen gehört: Clips, Takes, Playlists, Comps. Leere Ordner verschwinden. */
export function deleteTracks(p: Project, ids: string[]): { tracks: number; clips: number; takes: number } {
  const set = new Set(ids);
  const out = { tracks: p.tracks.filter((t) => set.has(t.id)).length, clips: p.clips.filter((c) => set.has(c.trackId)).length, takes: p.takes.filter((t) => set.has(t.trackId)).length };
  p.tracks = p.tracks.filter((t) => !set.has(t.id));
  p.clips = p.clips.filter((c) => !set.has(c.trackId));
  p.takes = p.takes.filter((t) => !set.has(t.trackId));
  for (const id of ids) { if (p.playlists) delete p.playlists[id]; if (p.comps) delete p.comps[id]; }
  if (p.folders) p.folders = p.folders.filter((f) => p.tracks.some((t) => t.folderId === f.id));
  return out;
}
/** Spuren duplizieren (direkt darunter): Einstellungen, Plugins, Automation und auf Wunsch die Clips. Gibt die neuen Ids zurück. */
export function duplicateTracks(p: Project, ids: string[], withClips: boolean): string[] {
  const made: string[] = [];
  for (const id of ids) {
    const i = p.tracks.findIndex((t) => t.id === id);
    if (i < 0) continue;
    const src = p.tracks[i];
    const t: Track = { ...structuredClone(src), id: uid('t'), name: `${src.name} Kopie`, armed: false, solo: false };   // Insert-Ids bleiben gleich, damit die Plugin-Automation (ins:<slot>:…) mitkommt
    p.tracks.splice(i + 1, 0, t);
    if (withClips) for (const c of p.clips.filter((x) => x.trackId === id)) p.clips.push({ ...structuredClone(c), id: uid('c'), trackId: t.id, groupId: undefined });
    made.push(t.id);
  }
  return made;
}

// ---- Ordner (Folder Tracks) ----
/** Spuren in einen neuen Ordner legen; sie rücken hinter die erste ausgewählte Spur zusammen */
export function makeFolder(p: Project, trackIds: string[], name = 'Ordner'): string | null {
  const kids = p.tracks.filter((t) => trackIds.includes(t.id) && t.kind !== 'bus');
  if (!kids.length) return null;
  const id = uid('f');
  (p.folders ??= []).push({ id, name, color: kids[0].color, open: true });
  for (const t of kids) t.folderId = id;
  const first = p.tracks.indexOf(kids[0]);
  const rest = p.tracks.filter((t) => !kids.includes(t));
  const at = rest.indexOf(p.tracks.slice(0, first).reverse().find((t) => !kids.includes(t)) as Track) + 1;
  p.tracks = [...rest.slice(0, at), ...kids, ...rest.slice(at)];
  // leere Ordner aufräumen
  p.folders = p.folders.filter((f) => p.tracks.some((t) => t.folderId === f.id));
  return id;
}
/** Ordner auflösen: Spuren bleiben, nur die Klammer fällt weg */
export function dissolveFolder(p: Project, folderId: string) {
  for (const t of p.tracks) if (t.folderId === folderId) delete t.folderId;
  p.folders = (p.folders ?? []).filter((f) => f.id !== folderId);
}
